import { ElementRef, FC, useEffect, useRef, useState } from "react";
import PaginateCard from "../PaginateCard";

import {
  faAt,
  faClock,
  faEllipsisVertical,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Dropdown, Row } from "react-bootstrap";
import { Link } from "react-router-dom";

import EmailLink from "@/components/atoms/EmailLink";
import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import Loader from "@/components/atoms/Loader";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import SoftBadge from "@/components/atoms/SoftBadge";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import {
  checkIfUserHasRoles,
  getColorFromRole,
  getReelRoleName,
} from "@/helpers/UserHelpers";
import { PaginateCardProps } from "@/interfaces/Card";
import { IFilters } from "@/interfaces/Fetch";
import { UserFilters } from "@/interfaces/IUser";
import {
  useDeleteCompanyUserMutation,
  useGetCompanyUsersQuery,
} from "@/services/companies/users";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import classNames from "classnames";
import { DateTime } from "luxon";
import { ToastContentProps, toast } from "react-toastify";
import AreYouSurToContinueModal from "../../modals/AreYouSurToContinueModal";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "@/services/slices/authSlice";

const CompanyUsersCard: FC<
  PaginateCardProps & IFilters & { companyId: string }
> = ({ onGetTotal, companyId, ...props }) => {
  const user = useSelector(selectCurrentUser);
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const [filters, setFilters] = useState<UserFilters>({
    pageIndex: 0,
    pageSize: 10,
    ...props,
  });

  const { data, isLoading, isError, error } = useGetCompanyUsersQuery([
    companyId,
    filters,
  ]);
  const [destroyCompanyUser] = useDeleteCompanyUserMutation();

  const onDelete = async (id: string) => {
    toast.promise(destroyCompanyUser([companyId, id]).unwrap(), {
      pending: "Suppression de l'utilisateur en cours ...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              L'utilisateur à bien été enlevé de l'entreprise 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  useEffect(() => {
    data?.meta && onGetTotal?.(data.meta.total);
  }, [data?.meta]);

  if (isLoading) {
    return <Loader />;
  } else if (isError) {
    return <ErrorComponent error={error} />;
  }

  return (
    <PaginateCard filters={filters} setFilters={setFilters} meta={data?.meta}>
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Êtes vous sûr de vouloir détacher l'utilisateur qui à pour id : {{id}}"
      />
      {data?.data.map(
        (
          { id, user: { phone, email, fullname }, userId, createdAt, role },
          index,
        ) => (
          <div
            key={id}
            className="bg-white dark__bg-1100 p-x1 rounded-3 shadow-sm"
          >
            <Row className="flex-between-center">
              <Col xs={10}>
                <Link to={`/users/${userId}`} className="fw-semi-bold">
                  {fullname}
                </Link>
              </Col>
              {checkIfUserHasRoles(user, ["ADMIN"]) && (
                <Col xs={"auto"} className="d-flex justify-content-end">
                  <Dropdown
                    align="end"
                    className="btn-reveal-trigger d-inline-block"
                  >
                    <Dropdown.Toggle split variant="nolina-default" size="sm">
                      <FontAwesomeIcon
                        icon={faEllipsisVertical}
                        className="fs--2"
                      />
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="border py-0">
                      <div className="py-2">
                        <Dropdown.Item
                          className="text-danger"
                          onClick={() =>
                            DeleteModalRef.current?.showOrHiddenModal(userId)
                          }
                        >
                          Supprimer
                        </Dropdown.Item>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              )}
            </Row>
            <div className="border-bottom border-lg-0 border-0 my-3"></div>
            <Row className="flex-between-center">
              <Col xs={12} lg={8}>
                <EmailLink email={email} className="h6" />
                {phone ? (
                  <PhoneCallLink
                    number={phone}
                    className="d-flex align-items-center gap-1 h6 mb-0 text-600"
                  />
                ) : (
                  <div className="d-flex align-items-center gap-1 h6 mb-0">
                    <FontAwesomeIcon icon={faPhone} transform="shrink-3 up-1" />
                    <UnknownBadge />
                  </div>
                )}
              </Col>

              <Col xs={12} lg={"auto"}>
                <div className="border-bottom border-lg-0 border-0 my-lg-0 my-3"></div>
                <Flex direction="column">
                  <SoftBadge
                    key={role}
                    bg={getColorFromRole([role])}
                    className={classNames({
                      "mt-1": index !== 0,
                    })}
                  >
                    {getReelRoleName([role])}
                  </SoftBadge>
                  <h6 className="mb-0 mt-2">
                    <span className="d-flex align-items-center justify-content-end gap-1">
                      <FontAwesomeIcon
                        icon={faClock}
                        transform="shrink-3 up-1"
                      />
                      <span className="text-600">
                        {DateTime.fromISO(createdAt)
                          .setLocale("fr")
                          .toFormat("dd LLL yyyy")}
                      </span>
                    </span>
                  </h6>
                </Flex>
              </Col>
            </Row>
          </div>
        ),
      )}
    </PaginateCard>
  );
};

export default CompanyUsersCard;
